/*
Copyright (c) Uber Technologies, Inc.

This source code is licensed under the MIT license found in the
LICENSE file in the root directory of this source tree.
*/
import * as React from 'react';
import { useBase2Features } from '../helpers/base2-features-context';
import { useThemeWithPotentialUpgrades } from '../helpers/hooks/useThemeWithPotentialUpgrades';
import { LightTheme } from '../themes';
import type { Base2Features, BaseProduct, ThemeMode } from '../helpers/types';
import type { Theme } from './types';
export const ThemeContext: React.Context<Theme> = React.createContext(LightTheme);
const getV2DataAttributes = ({
  colors,
  elevation,
  material,
  typography
}: Base2Features): Record<string, string> => ({
  ...(colors ? {
    'data-base-version-color': '2'
  } : {}),
  ...(elevation ? {
    'data-base-version-elevation': '2'
  } : {}),
  ...(material ? {
    'data-base-version-material': '2'
  } : {}),
  ...(typography ? {
    'data-base-version-typography': '2'
  } : {})
});
export type ThemeProviderProps = {
  theme: Theme;
  /** Opt in to CSS-driven theming. Falls back to `Base2Features` context when omitted. */
  cssTheming?: boolean;
  product?: BaseProduct;
  /** Color scheme. Falls back to `theme.themeMode` when omitted. */
  themeMode?: ThemeMode;
};

/**
 * Resolves Base 2.0 token upgrades and delivers the theme via `ThemeContext`.
 *
 * - JS-driven (default): v2 tokens are merged into the theme object as raw
 *   values; no DOM element is introduced.
 * - CSS-driven (`cssTheming`): renders a `display:contents` wrapper carrying
 *   `data-base-version-*` / `data-theme` / `data-product` so the
 *   `base-colors-v2.css` / `base-elevation-v2.css` cascade can scope tokens.
 */
const ThemeProvider: React.FC<React.PropsWithChildren<ThemeProviderProps>> = props => {
  const {
    theme,
    children,
    cssTheming,
    product,
    themeMode
  } = props;
  const base2Features = useBase2Features();

  // Prop wins; context is the fallback so the fusion plugin can opt in app-wide.
  const effectiveCssTheming = cssTheming ?? base2Features.cssTheming;

  // themeMode is conceptually part of the theme, so the prop is only an override.
  // `theme` can be omitted at runtime (BaseProvider's theme prop is sometimes
  // unset), so guard the access. `themeModeSignal` (un-defaulted) decides whether
  // there is anything to scope; `effectiveThemeMode` resolves to a concrete mode,
  // defaulting to light, for token resolution and the emitted `data-theme`.
  const themeModeSignal = themeMode ?? theme?.themeMode;
  const effectiveThemeMode = themeModeSignal ?? 'light';

  // product, like themeMode, is conceptually part of the theme, so the prop is
  // only an override. Unlike themeMode there is no default — most themes are
  // brand-less.
  const effectiveProduct = product ?? theme?.product;
  const upgradedTheme = useThemeWithPotentialUpgrades({
    theme,
    base2Features,
    cssTheming: effectiveCssTheming,
    themeMode: effectiveThemeMode,
    product: effectiveProduct
  });
  const content = <ThemeContext.Provider value={upgradedTheme}>{children}</ThemeContext.Provider>;
  if (!effectiveCssTheming) return content;
  const hasAnyV2 = base2Features.colors || base2Features.elevation || base2Features.material || base2Features.typography;
  if (!hasAnyV2 && !effectiveProduct && !themeModeSignal) return content;
  return <div style={{
    display: 'contents'
  }} {...getV2DataAttributes(base2Features)} data-theme={effectiveThemeMode} {...effectiveProduct ? {
    'data-product': effectiveProduct
  } : {}}>
      {content}
    </div>;
};
export default ThemeProvider;